import { z } from 'zod';
import { centAmount, isoDate, uuid } from '../posting.js';
import { DATENKATEGORIEN } from './felder.js';
import { datevExportRueckmeldungZeileSchema } from './rueckmeldung.js';
/**
 * Die Vertraege der DATEV-Exportmaske — Chip `DX-maske`, Task 13 des Plans
 * `docs/superpowers/plans/2026-09-08-datev-export-umsetzung.md`.
 *
 * Spec `docs/superpowers/specs/2026-09-08-datev-export-design.md`
 * Abschnitt 8.1 (die vier Schritte), 8.2 (das Register), 7.3 (`D-25`, der
 * Rueckkanal); `D-21` (zwei Faehigkeiten, zwei Personen), `D-30` (ohne gruen
 * kein Knopf).
 *
 * ===========================================================================
 * WARUM DIE ZWEI WERTEBEREICHE HIER STEHEN UND NICHT IM DIENST
 * ===========================================================================
 *
 * `datevExportKategorieSchema` und `datevExportStatusSchema` gab es bis zu
 * diesem Chip nur als handgeschriebene String-Literal-Union in
 * `apps/api/src/datev/register.service.ts`. Sobald die Oberflaeche dieselben
 * Werte braucht, muesste sie sie ein zweites Mal abschreiben — und genau die
 * Fehlerklasse jagt `pnpm api:enums:check`: Ein neuer Wert im einen Ort
 * aendert den anderen nie mit, weil beide Deklarationen fuer sich gueltig
 * sind. Die Wertebereiche stehen deshalb hier, an der einen Stelle, die
 * beide Seiten lesen; `register.service.ts` importiert sie und exportiert sie
 * unveraendert weiter, damit seine bisherigen Aufrufer nichts merken.
 *
 * **Die Liste ist dieselbe wie `datev_export_kategorie_bekannt` (`0187`)**
 * und wird nicht daneben gefuehrt: Ein sechster Wert dort ohne Zeile hier
 * faellt in `packages/schema/test/enforcement/datev-register.test.ts` auf.
 *
 * ===========================================================================
 * GELD IST NIEMALS `number` (HAUSREGEL 1)
 * ===========================================================================
 *
 * `summeSoll` und `summeHaben` gehen ueber {@link centAmount} und kommen als
 * `Money` heraus — dieselbe Behandlung wie in `zahlungszuordnung.ts`. Aus der
 * Datenbank kommen sie als `numeric(15,2)`-Zeichenkette; ein Umweg ueber
 * `number` verlore genau die Cents, um die es geht.
 */
/** Die fuenf Lieferungen aus Spec 3.1 — `datev_export.kategorie` (`0187`). */
export const datevExportKategorieSchema = z.enum([
    'buchungsstapel',
    'debitoren_kreditoren',
    'saldenvortrag',
    'kontenbeschriftungen',
    'belegbilder',
]);
/** Der Weg einer Registerzeile (`0187`): erzeugt -> freigegeben, oder verworfen. */
export const datevExportStatusSchema = z.enum(['erzeugt', 'freigegeben', 'verworfen']);
/**
 * Der Zustand einer geschlossenen Periode aus der Sicht `datev_export_offen`
 * (`0190`, Auskunft 1). **Arbeit und kein Verstoss** — jede Zeile ist ein
 * Monat, der noch aussteht.
 */
export const datevExportZustandSchema = z.enum([
    'ohne_stapel',
    'nur_erzeugt',
    'nicht_versandt',
]);
/** Die Datenkategorie der DATEI — nicht dasselbe wie die Registerkategorie. */
export const datevDatenkategorieSchema = z.enum(DATENKATEGORIEN);
/**
 * Eine Zeile des Registers `datev_export`, in der Form, die ueber die
 * Schnittstelle geht.
 *
 * **`erzeugtAm` und die drei anderen Zeitpunkte sind ISO-Zeichenketten und
 * keine `Date`.** Die Antwort ist JSON; ein `Date` waere dort ohnehin eine
 * Zeichenkette, und ein Vertrag, der etwas anderes behauptet als das, was
 * ankommt, ist schlechter als keiner.
 *
 * **`zeitraumVon`/`zeitraumBis` sind Kalendertage** (`JJJJ-MM-TT`) — ein
 * Kalendertag hat keine Zeitzone, und ein Zeitstempel gaebe ihm eine.
 */
export const datevExportZeileAnsichtSchema = z
    .object({
    id: uuid,
    periodId: uuid,
    jahr: z.number().int(),
    monat: z.number().int().min(1).max(12),
    kategorie: datevExportKategorieSchema,
    zeitraumVon: isoDate,
    zeitraumBis: isoDate,
    formatversion: z.string().min(1),
    satzzahl: z.number().int().min(0),
    summeSoll: centAmount,
    summeHaben: centAmount,
    sha256: z.string().regex(/^[0-9a-f]{64}$/, 'Erwartet werden 64 hexadezimale Zeichen.'),
    documentId: uuid.nullable(),
    status: datevExportStatusSchema,
    erzeugtVon: z.string().min(1),
    erzeugtAm: z.string().min(1),
    freigegebenVon: z.string().min(1).nullable(),
    freigegebenAm: z.string().min(1).nullable(),
    /**
     * Einzelfreigabe (`D-31`, `0263`): Wer den Stapel erzeugt hat, hat ihn auch
     * freigegeben — nur unter einer Freigaberegel ohne vier Augen; das
     * Nachweisblatt zählt solche Freigaben je Periode. `optional()`, weil
     * ältere Fixturen das Feld nicht führen.
     */
    einzelfreigabe: z.boolean().optional(),
    versandtAm: z.string().min(1).nullable(),
    verworfenVon: z.string().min(1).nullable(),
    verworfenAm: z.string().min(1).nullable(),
    verwerfungsgrund: z.string().min(1).nullable(),
})
    .strict();
/** Das Register, neueste zuerst. */
export const datevExportListeSchema = z
    .object({ exporte: z.array(datevExportZeileAnsichtSchema) })
    .strict();
/**
 * Eine geschlossene Periode mit ihrem Exportstand — Schritt 1 der Maske
 * (Spec 8.1: nur geschlossene, noch nicht exportierte Perioden aus
 * `datev_export_offen`).
 *
 * **`exportierbar` ist eine Ableitung und keine zweite Wahrheit.** Sie sagt
 * allein, ob die Periode geschlossen und noch ohne gueltigen Buchungsstapel
 * ist; ob die sechzehn Vorpruefungen halten, sagt erst die Vorschau
 * (`D-30`). Die Maske schaltet den Erzeugen-Knopf deshalb nicht nach diesem
 * Feld frei, sondern nach dem Urteil der Vorschau.
 */
export const datevExportPeriodeSchema = z
    .object({
    periodeId: uuid,
    jahr: z.number().int(),
    monat: z.number().int().min(1).max(12),
    zustand: datevExportZustandSchema,
    geschlossenAm: z.string().min(1).nullable(),
    exportierbar: z.boolean(),
    /** Die gueltige Buchungsstapel-Zeile, falls es schon eine gibt. */
    buchungsstapel: datevExportZeileAnsichtSchema.nullable(),
})
    .strict();
export const datevExportPeriodenListeSchema = z
    .object({ perioden: z.array(datevExportPeriodeSchema) })
    .strict();
/** Eine Zeile einer Kontrollsicht, in den sechs einheitlichen Spalten aus `0189`. */
export const datevVorpruefungBefundSchema = z
    .object({
    befund: z.string().min(1),
    bezug: z.string().min(1),
    einzelheit: z.string(),
})
    .strict();
/**
 * Das Ergebnis EINER der sechzehn Vorpruefungen. Leer ist bestanden, jede
 * Zeile ein Fehlschlag — und jede rote Zeile nennt ihren `bezug`, also die
 * Belegnummer oder die Kontonummer (Spec 8.1, Schritt 2).
 */
export const datevVorpruefungErgebnisSchema = z
    .object({
    sicht: z.string().min(1),
    global: z.boolean(),
    zeilen: z.array(datevVorpruefungBefundSchema),
    uebergangen: z.number().int().min(0),
    ausnahme: z.string().min(1).nullable(),
    bestanden: z.boolean(),
})
    .strict();
/** Ein benannter Befund ausserhalb der sechzehn Kontrollsichten. */
export const datevStapelBefundSchema = z
    .object({
    art: z.string().min(1),
    bezug: z.string(),
    meldung: z.string().min(1),
})
    .strict();
/**
 * Eine Lieferung in der Ansicht — **ohne die Bytes**.
 *
 * Die Datei selbst geht nie durch diese Antwort: Sie waere im Trockenlauf
 * eine Datei, die es nicht geben darf (`D-30`), und nach dem Erzeugen liegt
 * sie im Archiv, wo der Download sie holt. Was hier steht, sind die Zahlen,
 * an denen ein Mensch die Lieferung prueft: Satzzahl, Summen und der Digest.
 */
export const datevLieferungSchema = z
    .object({
    kategorie: datevExportKategorieSchema,
    datenkategorie: datevDatenkategorieSchema,
    dateiname: z.string().min(1),
    sha256: z.string().regex(/^[0-9a-f]{64}$/, 'Erwartet werden 64 hexadezimale Zeichen.'),
    satzzahl: z.number().int().min(0),
    summeSoll: centAmount,
    summeHaben: centAmount,
    formatversion: z.string().min(1),
    uebergangen: z.number().int().min(0),
    registerId: uuid.nullable(),
})
    .strict();
/** Die Gegenzahlen aus dem Journal selbst, in SQL gerechnet. */
export const datevJournalzahlenSchema = z
    .object({
    buchungen: z.number().int().min(0),
    zeilen: z.number().int().min(0),
    summeSoll: centAmount,
    summeHaben: centAmount,
})
    .strict();
/**
 * Was Vorschau und Erzeugen liefern — ein diskriminiertes Paar ueber `ok`,
 * genau wie `StapelErgebnis` im Dienst.
 *
 * **Der Abbruchfall ist kein Fehler der Anfrage und deshalb keine 4xx.** Ein
 * Befund der Vorpruefung ist das Ergebnis dieses Laufs; er kommt mit `200`
 * und `ok: false` heraus, samt der vollstaendigen Liste der sechzehn
 * Pruefungen — auch der bestandenen, weil die Maske alle zeigt.
 */
export const datevStapelAnsichtSchema = z.discriminatedUnion('ok', [
    z
        .object({
        ok: z.literal(false),
        grund: z.string().min(1),
        meldung: z.string().min(1),
        vorpruefung: z.array(datevVorpruefungErgebnisSchema),
        befunde: z.array(datevStapelBefundSchema),
    })
        .strict(),
    z
        .object({
        ok: z.literal(true),
        trockenlauf: z.boolean(),
        periode: z
            .object({
            id: uuid,
            jahr: z.number().int(),
            monat: z.number().int().min(1).max(12),
            von: isoDate,
            bis: isoDate,
        })
            .strict(),
        erzeugtAm: z.string().min(1),
        vorpruefung: z.array(datevVorpruefungErgebnisSchema),
        lieferungen: z.array(datevLieferungSchema),
        journal: datevJournalzahlenSchema,
        belegplan: z
            .object({
            eintraege: z.number().int().min(0),
            kollisionen: z.number().int().min(0),
        })
            .strict(),
        buchungen: z.number().int().min(0),
        saetze: z.number().int().min(0),
    })
        .strict(),
]);
/**
 * Die Eingabe von Vorschau und Erzeugen: ein Monat, mehr nicht (`D-26`, ein
 * Monat je Lauf).
 */
export const datevExportErzeugenEingabeSchema = z
    .object({
    jahr: z.number().int().min(2000).max(2100),
    monat: z.number().int().min(1).max(12),
})
    .strict();
/**
 * Die Eingabe des Verwerfens (`D-23`): ein Grund, und er ist Pflicht.
 *
 * Ein Verwerfen ohne Grund waere ein Loeschen mit Zeitstempel — und wer
 * loeschen koennte, koennte behaupten, es habe den Stapel nie gegeben.
 */
export const datevExportVerwerfenEingabeSchema = z
    .object({ grund: z.string().trim().min(1, 'Ein Verwerfungsgrund ist Pflicht.') })
    .strict();
/** Die erfassten Rueckmeldungen eines Stapels, aelteste zuerst (anfuegend, `0188`). */
export const datevExportRueckmeldungListeSchema = z
    .object({ rueckmeldungen: z.array(datevExportRueckmeldungZeileSchema) })
    .strict();
/**
 * Ein Stapel mit allem, was an ihm haengt — die Einzelauskunft.
 *
 * **Die Rueckmeldungen stehen HIER und nicht in der Liste.** Sie sind je
 * Stapel wenige Zeilen und je Register viele; eine Liste, die sie mitfuehrte,
 * laedt fuer jede Zeile etwas, das nur an einer gebraucht wird.
 */
export const datevExportDetailSchema = z
    .object({
    export: datevExportZeileAnsichtSchema,
    rueckmeldungen: z.array(datevExportRueckmeldungZeileSchema),
})
    .strict();
//# sourceMappingURL=maske.js.map